import { styled, pxToRem } from '@withjoy/joykit/theme';
import { ParticleJoyLogo } from './ParticleJoyLogo';

/**
 * `ParticleJoyLogo` at an explicit size — the one place that reaches into the logo's internals to
 * size it. Size + visibility are parameterised so the same styled logo serves the admin header
 * (hidden below `md`, where the hamburger alone carries the branding), the unified
 * website-editor nav, the burger drawer's home link and Service Center's header. It lives in
 * `@shared` because Service Center sits in `@apps/card`, which may not import `@apps/admin`;
 * `BurgerLogoTrigger.styles` re-exports it as `StyledParticleJoyLogo` for admin's call sites.
 *
 * The svg and the canvas have to be sized together: the canvas wrapper's clip path is an
 * `objectBoundingBox` clip measured from the svg's parent div, so sizing only one leaves the clip
 * region and the painted mark disagreeing (CORE-6518). `display: block` drops the svg's line box,
 * which would otherwise make that div taller than the logo and stretch the mark.
 *
 * The canvas's height is forced alongside its width: its intrinsic 54×35 ratio is not the svg's
 * 54×34, so width alone leaves the canvas ~0.9px taller than the svg box it is clipped to and the
 * mark stands proud at the bottom. Admin's header picks this up too — the logo loses that sub-pixel
 * overhang there as well, which is the fix, not a regression.
 */
export const SizedParticleJoyLogo = styled(ParticleJoyLogo)<{
  $width: number;
  $height: number;
  $alwaysVisible: boolean;
  $translateY?: number;
}>`
  display: ${({ $alwaysVisible }) => ($alwaysVisible ? 'block' : 'none')};

  ${({ theme, $alwaysVisible }) =>
    !$alwaysVisible &&
    `${theme.mediaQueries.md} {
      display: block;
    }`}

  ${({ $translateY }) => $translateY !== undefined && `transform: translateY(${pxToRem($translateY)});`}

  & > div > svg {
    display: block;
    width: ${({ $width }) => pxToRem($width)};
    height: ${({ $height }) => pxToRem($height)};
  }

  & canvas {
    width: ${({ $width }) => pxToRem($width)} !important;
    /* 54×35 canvas vs 54×34 svg: without this the canvas stands ~0.9px proud at the bottom. */
    height: ${({ $height }) => pxToRem($height)} !important;
  }
`;
